import React from "react";
import { Card, Typography, Stack, Avatar, Checkbox, Chip } from "@mui/material";
import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";

const ProductItemModal = ({
  item,
  disabled,
  // Bulk selection props (CategoryProductsTab → ProductSelector):
  selected = false,
  onToggleSelect,
  alreadyAdded = false,
  defaultLanguage,
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = defaultLanguage || i18n.language;

  const productName =
    item?.productName?.[currentLanguage] || item?.name || "Unnamed Product";
  const productDescription =
    item?.description?.[currentLanguage] || item?.description || "";
  const rawImage = item?.image || item?.customProductImageUrl || "";
  const productPrice = item?.price || item?.priceValue || "";
  const productCode = item?.code || item?.productCode || "";
  const productCategory =
    item?.category?.name?.[currentLanguage] || item?.categoryName || "";

  // Sanitize obviously invalid filenames like BEEF-SOUP-.png → block loading
  const productImage = React.useMemo(() => {
    const url = String(rawImage || "");
    if (!url) return "";
    if (/-\.(png|jpe?g|webp|gif)$/i.test(url)) return "";
    return url;
  }, [rawImage]);

  const [imgError, setImgError] = React.useState(false);
  const initial =
    typeof productName === "string" && productName.length > 0
      ? productName.charAt(0).toUpperCase()
      : "";

  const handleToggle = () => {
    if (disabled || alreadyAdded) return;
    if (onToggleSelect) onToggleSelect(item);
  };

  return (
    <Card
      onClick={handleToggle}
      sx={{
        mb: 2,
        p: 2,
        cursor: alreadyAdded || disabled ? "default" : "pointer",
        borderStyle: "solid",
        borderWidth: selected ? 2 : 1,
        borderColor: selected ? "primary.main" : "divider",
        opacity: alreadyAdded ? 0.55 : 1,
        transition: "border-color 0.15s, background-color 0.15s",
        bgcolor: selected ? "action.selected" : "background.paper",
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        {alreadyAdded ? (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={t("pages.dsokCategories.alreadyInCategory", {
              defaultValue: "V kategórii",
            })}
            sx={{ flexShrink: 0 }}
          />
        ) : (
          <Checkbox
            checked={selected}
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
            onChange={handleToggle}
            sx={{ flexShrink: 0 }}
            inputProps={{ "aria-label": productName }}
          />
        )}
        <Avatar
          src={imgError || !productImage ? undefined : productImage}
          alt={productName}
          variant="rounded"
          sx={{ width: 64, height: 64, flexShrink: 0 }}
          imgProps={{ onError: () => setImgError(true) }}
        >
          {initial}
        </Avatar>
        <Div sx={{ flex: 1 }}>
          <Typography variant="h6" gutterBottom>
            {productName}
          </Typography>

          {productCode && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>{t("pages.products.code")}:</strong> {productCode}
            </Typography>
          )}

          {productCategory && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>{t("pages.products.category")}:</strong> {productCategory}
            </Typography>
          )}

          {productDescription && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {productDescription}
            </Typography>
          )}

          {productPrice && (
            <Typography
              variant="body2"
              color="primary"
              sx={{ fontWeight: "bold" }}
            >
              {t("pages.products.price")}: {productPrice}
            </Typography>
          )}
        </Div>
      </Stack>
    </Card>
  );
};

export default ProductItemModal;
